import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { LogOut, ArrowLeft, GraduationCap, User, Loader2, HelpCircle, Crown } from "lucide-react";
import { isAdmin, isDirectorOrAbove } from "@/lib/roles";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTrainingMode } from "@/contexts/TrainingModeContext";
import { NovatusLogo } from "@/components/NovatusLogo";
import { NotificationBell } from "@/components/NotificationBell";
import { IntakeReadyBadge } from "@/components/IntakeReadyBadge";
import { EnvBadge } from "@/components/EnvBadge";
import { TenantSwitcher } from "@/components/TenantSwitcher";

const roleLabel = {
  super_admin: "Super-Admin",
  admin: "Administrator",
  sales_director: "Vertriebsdirektor",
  sales_manager: "Vertriebsleiter",
  branch_manager: "Niederlassungsleiter",
  sales_rep: "Vertrieb",
  telesales_director: "Telesales-Direktor",
  agent: "Telesales-Agent",
  installer: "Installation",
};

export const AppHeader = ({ title, back, right }) => {
  const { user, logout } = useAuth();
  const { enabled: schulung, setEnabled: setSchulung } = useTrainingMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [schulungPending, setSchulungPending] = useState(false);
  const canToggleSchulung = user?.role === "admin" || user?.role === "sales_director";

  // Iter88zbl (2026-02-19) — Smart-Back:
  // Wenn der User innerhalb der App navigiert ist (React-Router setzt dann
  // eine `location.key`, die nicht `"default"` ist), gehen wir per
  // `navigate(-1)` einen Schritt zurück — dadurch bleibt der State (z.B.
  // Formular-Eingaben, Scroll-Position) der vorherigen Seite erhalten.
  // Fallback (Direkt-Link, F5, PWA-Kaltstart): explizit übergebener
  // `back`-Pfad, damit man nie im Leeren landet.
  const handleBack = () => {
    const hasHistory = location.key && location.key !== "default";
    if (hasHistory) {
      navigate(-1);
      return;
    }
    if (typeof back === "string" && back) {
      navigate(back);
      return;
    }
    navigate("/dashboard");
  };

  // Iter88zbm (2026-02-19) — Zurück-Taste global: immer sichtbar, außer auf
  // den echten Home-Routen (Dashboard, Telesales-Cockpit, Login, Root). Der
  // User verliert seine Eingaben nicht mehr, weil er nicht immer über das
  // Hauptmenü umwegen muss. Explizit `back={false}` deaktiviert die Taste;
  // `back={true}` erzwingt sie auch auf Home-Routen.
  const HOME_ROUTES = new Set(["/", "/dashboard", "/telesales", "/login"]);
  const isHomeRoute = HOME_ROUTES.has(location.pathname);
  const showBack =
    back === false ? false
    : isHomeRoute ? back === true
    : true;

  const handleSchulungToggleOff = async () => {
    if (!canToggleSchulung || schulungPending) return;
    if (!window.confirm("Schulungs-Modus DEAKTIVIEREN?\n\nEchte Projekte sind sofort wieder für alle Nutzer sichtbar.")) return;
    setSchulungPending(true);
    try {
      await setSchulung(false);
      toast.success("Schulungs-Modus deaktiviert", { description: "Echte Vertriebsdaten sind wieder sichtbar.", duration: 4000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wechsel fehlgeschlagen");
    } finally {
      setSchulungPending(false);
    }
  };

  const ChipInner = (
    <>
      {schulungPending ? <Loader2 size={11} className="animate-spin" /> : <GraduationCap size={11} />}
      Schulung
    </>
  );
  // Desktop / Tablet (>= sm): voller „SCHULUNG"-Text neben dem Logo
  const chipBaseClass = "hidden sm:inline-flex items-center gap-1.5 text-[12px] font-bold tracking-[0.14em] uppercase px-2 py-0.5 rounded-md bg-violet-500/15 text-violet-300 ring-1 ring-violet-500/40";
  // Mobile (< sm): kompakter Icon-Button in der rechten Action-Reihe
  const chipMobileClass = "sm:hidden h-11 w-11 rounded-xl bg-violet-500/15 ring-1 ring-violet-500/40 text-violet-300 flex items-center justify-center transition active:scale-95 disabled:opacity-60";

  return (
    <header
      data-schulung={schulung ? "1" : "0"}
      className={`relative xl:sticky top-0 z-40 bg-[#09090B]/85 backdrop-blur-xl border-b ${schulung ? "border-violet-500/30" : "border-zinc-800"}`}
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex flex-wrap xl:flex-nowrap items-center justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 xl:flex-1">
          {showBack ? (
            <button
              data-testid="app-back-btn"
              onClick={handleBack}
              title="Zurück zur vorherigen Seite"
              aria-label="Zurück"
              className="h-11 w-11 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center transition active:scale-95 flex-shrink-0"
            >
              <ArrowLeft size={20} />
            </button>
          ) : null}
          <Link
            to="/dashboard"
            data-testid="app-logo-link"
            className="flex-shrink-0 min-w-0"
          >
            <span className="block sm:hidden"><NovatusLogo size="md" compact variant="mobile" /></span>
            <span className="hidden sm:block"><NovatusLogo size="lg" /></span>
          </Link>
          <EnvBadge />
          {schulung && (
            canToggleSchulung ? (
              <button
                type="button"
                onClick={handleSchulungToggleOff}
                disabled={schulungPending}
                data-testid="training-mode-chip"
                title="Antippen, um den Schulungs-Modus zu deaktivieren"
                aria-label="Schulungs-Modus deaktivieren"
                className={`${chipBaseClass} hover:bg-violet-500/25 hover:ring-violet-500/60 active:scale-95 transition disabled:opacity-60 cursor-pointer`}
              >
                {ChipInner}
              </button>
            ) : (
              <span
                data-testid="training-mode-chip"
                title="Schulungs-Modus aktiv – nur Demo-Daten sichtbar"
                className={chipBaseClass}
              >
                {ChipInner}
              </span>
            )
          )}
          {title && <h1 className="font-display font-semibold text-base sm:text-lg truncate ml-1 hidden lg:block">{title}</h1>}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 flex-wrap justify-end ml-auto max-w-full">
          {schulung && canToggleSchulung && (
            <button
              type="button"
              onClick={handleSchulungToggleOff}
              disabled={schulungPending}
              data-testid="training-mode-chip-mobile"
              title="Schulungs-Modus deaktivieren"
              aria-label="Schulungs-Modus deaktivieren"
              className={`${chipMobileClass} hover:bg-violet-500/25`}
            >
              {schulungPending ? <Loader2 size={16} className="animate-spin" /> : <GraduationCap size={16} />}
            </button>
          )}
          {schulung && !canToggleSchulung && (
            <span
              data-testid="training-mode-chip-mobile"
              title="Schulungs-Modus aktiv"
              aria-label="Schulungs-Modus aktiv"
              className={chipMobileClass}
            >
              <GraduationCap size={16} />
            </span>
          )}
          {right}
          {isDirectorOrAbove(user) && (
            <>
              <Link
                to="/admin/tenants"
                data-testid="admin-tenants-nav-link"
                title="Mandanten / Unternehmen verwalten"
                className="inline-flex items-center gap-1 sm:gap-1.5 h-9 px-2 sm:px-3 rounded-lg bg-gradient-to-r from-amber-500/15 to-orange-500/15 hover:from-amber-500/25 hover:to-orange-500/25 ring-1 ring-amber-500/40 text-amber-200 text-[12px] sm:text-xs font-bold tracking-wider transition active:scale-95"
              >
                <Crown size={13} className="text-amber-300" />
                <span className="hidden xl:inline">MANDANTEN</span>
              </Link>
              {(user?.is_super_admin || (user?._memberships_count ?? 0) > 1) && <TenantSwitcher />}
            </>
          )}
          <IntakeReadyBadge />
          <NotificationBell />
          <button
            data-testid="app-help-btn"
            onClick={() => window.dispatchEvent(new CustomEvent("novatus:help-open"))}
            className="h-11 w-11 rounded-xl bg-[#00E575]/12 hover:bg-[#00E575]/20 text-[#00E575] flex items-center justify-center transition active:scale-95 ring-1 ring-[#00E575]/30"
            aria-label="Hilfe zu diesem Bereich"
            title="Hilfe zu diesem Bereich"
          >
            <HelpCircle size={18} strokeWidth={2.2} />
          </button>
          {user && (
            <button
              data-testid="app-profile-link"
              onClick={() => navigate("/profile")}
              className="hidden xl:flex items-center gap-2 px-3 py-1.5 bg-zinc-800/60 hover:bg-zinc-800 rounded-lg transition active:scale-95"
              title="Mein Profil"
            >
              <User size={14} className="text-[#00E575]" />
              <div className="text-xs text-left">
                <div className="font-medium leading-tight">{user.first_name} {user.last_name}</div>
                <div className="text-[12px] text-zinc-400 font-mono uppercase tracking-wider">{roleLabel[user.role]}</div>
              </div>
            </button>
          )}
          <button
            data-testid="app-logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="h-11 w-11 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center transition active:scale-95"
            aria-label="Logout"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
      {title && <div className="lg:hidden border-t border-zinc-800/50 bg-[#0E0E12]"><div className="max-w-6xl mx-auto px-4 py-2 text-[13px] font-display font-semibold text-zinc-200 truncate" data-testid="app-header-title-mobile">{title}</div></div>}
    </header>
  );
};
